import { Fragment, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { Dialog, Disclosure, Tab, Transition } from "@headlessui/react";

import { ReactComponent as Download } from "../assets/icons/Download.svg";
import { ReactComponent as Chevron } from "../assets/icons/Chevron.svg";
import { ReactComponent as Info } from "../assets/icons/Info.svg";
import { ReactComponent as Caret } from "../assets/icons/Caret.svg";
import DiabetesRiskScore from "../assets/images/scores/DiabetesRiskScore.png";
import BloodPressureScore from "../assets/images/scores/BloodPressure.png";
import HeartHealthScore from "../assets/images/scores/HeartHealth.png";
import MentalWellbeingScore from "../assets/images/scores/MentalWellbeing.png";
import PhysiologicalScore from "../assets/images/scores/Physiological.png";

import Cover from "../assets/images/report/Cover.png";
import CompanyLogo from "../assets/images/companyLogo.png";
import CareplixLogo from "../assets/images/CareplixLogo.png";
import Pointer from "../assets/images/report/Pointer.png";
import HeartHealth from "../assets/images/report/heart-health-score.png";
import MentalWellbeing from "../assets/images/report/mental-wellbeing-score.png";
import Physiological from "../assets/images/report/physiological-score.png";
import Diabetes from "../assets/images/report/Diabetes.png";
import BloodPressure from "../assets/images/report/blood-pressure.png";
import HeartRate from "../assets/images/report/heart-rate.png";
import RespirationRate from "../assets/images/report/respiration-rate.png";
import OxygenSaturation from "../assets/images/report/oxygen-saturation.png";
import BMI from "../assets/images/report/bmi.png";
import HRV from "../assets/images/report/hrv.png";
import HRRelated from "../assets/images/report/hr-related.png";
import CardiacOutput from "../assets/images/report/cardiac-output.png";
import MAP from "../assets/images/report/map.png";
import HeartUtilization from "../assets/images/report/heart-utilization.png";
import StressIndex from "../assets/images/report/stress-index.png";
import VO2MAX from "../assets/images/report/vo2max.png";
import { ReactComponent as HbA1cOptimal } from "../assets/icons/HbA1cOptimal.svg";
import { ReactComponent as HbA1cNonOptimal } from "../assets/icons/HbA1cNonOptimal.svg";

import {
  Font,
  Document,
  Page,
  StyleSheet,
  View,
  Text,
  Image,
  PDFDownloadLink,
} from "@react-pdf/renderer";

import { constants } from "../utils/ranges";

Font.register({
  family: "Poppins",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLDz8V1tvFP-KUEg.ttf",
      fontWeight: "light",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrFJDUc1NECPY.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLGT9V1tvFP-KUEg.ttf",
      fontWeight: "medium",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLEj6V1tvFP-KUEg.ttf",
      fontWeight: "semibold",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLCz7V1tvFP-KUEg.ttf",
      fontWeight: "bold",
    },
  ],
});
Font.registerHyphenationCallback((word) => [word]);

const PDFReport = ({
  AgentCode = "",
  dayCode = "",
  age = 0,
  gender = "",
  height = 0,
  weight = 0,
  result = {
    timestamp: new Date(),
    scan_mode: "",
    posture: "resting",
    mind_score: 0,
    heart_score: 0,
    physiological_score: 0,
    bp_sys: 0,
    bp_dia: 0,
    resp_rate: 0,
    oxy_sat_prcnt: 0,
    heart_rate: 0,
    bmi: 0,
    sdnn: 0,
    rmssd: 0,
    pNN50_per: 0,
    cardiac_out: 0.0,
    map: 0,
    heart_utilized: 0,
    HRMax: 0,
    HRR: 0,
    THRR: 0,
    vo2max: 0.0,
    diabetes_control_score: 0,
    hba1c: 0,
  },
}) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: "#fff",
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "space-between",
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 40,
      paddingVertical: 16,
    },
    contentContainer: { flex: 1, paddingHorizontal: 40 },
    title: {
      fontFamily: "Poppins",
      fontWeight: "bold",
      fontSize: 14,
      color: "#203A82",
      marginBottom: 16,
    },
    horizontalLine: {
      marginVertical: 8,
      height: 1,
      backgroundColor: "#D5D5D5",
      borderRadius: 999,
    },
    scoreTitle: { flex: 1, fontFamily: "Poppins", fontSize: 12 },
    trContainer: {
      flexDirection: "row",
      gap: 4,
      paddingVertical: 6,
      paddingHorizontal: 12,
    },
    trText: { flex: 1, fontSize: 10, fontFamily: "Poppins" },
    pTitle: {
      fontFamily: "Poppins",
      fontWeight: "semibold",
      fontSize: 12,
      color: "#233F8C",
      marginBottom: 4,
    },
    pDesc: {
      fontFamily: "Poppins",
      fontSize: 10,
      lineHeight: 1.5,
      textAlign: "justify",
    },
    pRange: {
      fontFamily: "Poppins",
      fontSize: 10,
      lineHeight: 1.25,
      textAlign: "justify",
    },
    footer: {
      paddingVertical: 20,
      paddingHorizontal: 40,
      backgroundColor: "#233F8C",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    },
    footerText: {
      fontFamily: "Poppins",
      fontWeight: "medium",
      fontSize: 8,
      color: "#fff",
    },
  });

  const Disclaimer = ({ containerStyle = {} }) => (
    <View style={[{ marginTop: 36 }, containerStyle]}>
      <Text
        style={{ fontFamily: "Poppins", fontWeight: "semibold", fontSize: 10 }}
      >
        Disclaimer
      </Text>
      <Text
        style={{
          fontFamily: "Poppins",
          fontSize: 8,
          lineHeight: 1.25,
          textAlign: "justify",
        }}
      >
        For Investigational Use Only. Careplix is not a substitute for the
        clinical judgment of a health care professional. Careplix is intended to
        improve your awareness of general wellness. Careplix does not diagnose,
        treat, mitigate or prevent any disease, symp-tom, disorder or abnormal
        physical state. Consult with a health care professional or emergency
        services if you believe you may have a medical issue.
      </Text>
    </View>
  );

  const Range = ({ ranges = [], selectedIndex = -1, containerStyle = {} }) => (
    <View style={[{ flexDirection: "row" }, containerStyle]}>
      {ranges.map((r, index, list) => (
        <View key={index} style={{ flex: 1, alignItems: "center" }}>
          <Text
            style={{
              color: r.color,
              fontFamily: "Poppins",
              fontSize: 8,
              marginBottom: 6,
              maxLines: 1,
              textOverflow: "ellipsis",
              textTransform: "uppercase",
            }}
          >
            {r.label}
          </Text>
          <View
            style={{
              alignSelf: "stretch",
              height: 6,
              backgroundColor: r.color,
              borderTopLeftRadius: index > 0 ? 0 : 20,
              borderBottomLeftRadius: index > 0 ? 0 : 20,
              borderTopRightRadius: index < list.length - 1 ? 0 : 20,
              borderBottomRightRadius: index < list.length - 1 ? 0 : 20,
            }}
          />
          {selectedIndex === index && (
            <Image source={Pointer} style={{ height: 8, marginTop: 4 }} />
          )}
        </View>
      ))}
    </View>
  );

  const Score = ({ icon, title = "", value = 0 }) => {
    const rangeIndex = value >= 75 ? 0 : value >= 50 ? 1 : 2;
    const selectedRange = constants["Scores"].ranges[rangeIndex];
    return (
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <Image
          source={icon}
          style={{ height: 20, width: 20, marginRight: 10 }}
        />
        <Text style={styles.scoreTitle}>{title}</Text>
        <Text
          style={[
            styles.scoreTitle,
            {
              color: selectedRange.color,
              fontWeight: "semibold",
              textTransform: "uppercase",
            },
          ]}
        >
          {selectedRange.label} Risk
        </Text>
        <Range
          containerStyle={{ flexBasis: "45%" }}
          ranges={constants["Scores"].ranges}
          selectedIndex={rangeIndex}
        />
      </View>
    );
  };

  const Table = ({ containerStyle = {}, title = "", data = [] }) => (
    <View style={containerStyle}>
      <View
        style={[
          styles.trContainer,
          {
            borderTopLeftRadius: 5,
            borderTopRightRadius: 5,
            backgroundColor: "#dee2ee",
          },
        ]}
      >
        <Text style={[styles.trText, { fontWeight: "semibold" }]}>{title}</Text>
        <Text style={[styles.trText, { fontWeight: "semibold" }]}>Value</Text>
        <Text style={[styles.trText, { fontWeight: "semibold" }]}>
          Measurement Scale
        </Text>
        <Text style={[styles.trText, { fontWeight: "semibold" }]}>Range</Text>
      </View>
      {data?.map?.(([title, value, scale, range], index) => (
        <View
          key={index}
          style={[
            styles.trContainer,
            { backgroundColor: Boolean(index % 2) ? "#f4f5f9" : "transparent" },
          ]}
        >
          <Text style={styles.trText}>{title}</Text>
          <Text style={styles.trText}>{value}</Text>
          <Text style={[styles.trText, { color: scale?.color ?? "#000" }]}>
            {scale?.label}
          </Text>
          <Text style={styles.trText}>{range}</Text>
        </View>
      ))}
    </View>
  );

  const ParameterInfo = ({
    containerStyle = {},
    icon,
    title = "",
    desc = "",
    ranges = [],
  }) => (
    <View
      style={[
        { flexDirection: "row", marginBottom: ranges.length > 0 ? 16 : 24 },
        containerStyle,
      ]}
    >
      <Image source={icon} style={{ height: 30, width: 30, marginRight: 10 }} />
      <View style={{ flex: 1 }}>
        <Text style={styles.pTitle}>{title}</Text>
        <Text style={styles.pDesc}>{desc}</Text>
        {ranges.map((r, idx) => (
          <View
            key={`range-${idx}`}
            style={{ marginTop: 4, flexDirection: "row" }}
          >
            <Text style={[styles.pRange, { marginHorizontal: 4 }]}>•</Text>
            <Text style={[styles.pRange, { flex: 1 }]}>
              <Text style={{ fontWeight: "semibold" }}>{r.label}: </Text>
              <Text>{r.desc}</Text>
            </Text>
          </View>
        ))}
      </View>
    </View>
  );

  const bpRiskSelIdx = useMemo(
    () =>
      result.bp_sys < 108 || result.bp_sys > 135
        ? 2
        : result.bp_sys < 115 || result.bp_sys > 125
          ? 1
          : 0,
    [result.bp_sys],
  );

  const diabetesRiskSelIdx = useMemo(
    () =>
      result.diabetes_control_score >= 75
        ? 0
        : result.diabetes_control_score >= 50
          ? 1
          : 2,
    [result.diabetes_control_score],
  );

  return (
    <Document
      title="CarePlix Report"
      subject="Report generated from CarePlix Vitals Scan"
      author="CarePlix"
    >
      <Page wrap={false}>
        <View style={{ position: "relative", height: "100%", width: "100%" }}>
          <Image
            source={Cover}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              objectFit: "fill",
            }}
          />
          {/* <Image
            source={CompanyLogo}
            style={{ position: "absolute", top: 40, left: 56, width: 200 }}
          /> */}
          <Image
            source={CareplixLogo}
            style={{ position: "absolute", bottom: 72, right: 48, width: 160 }}
          />
          <Text
            style={{
              marginTop: 168,
              fontFamily: "Poppins",
              fontWeight: "bold",
              fontSize: 36,
              color: "#fff",
              textAlign: "center",
            }}
          >
            {"Digital Health\nAssessment Report"}
          </Text>
        </View>
      </Page>
      <Page style={styles.page}>
        <View fixed style={styles.header}>
          <Image source={CompanyLogo} style={{ height: 60, width: 200 }} />
          <View style={{ alignItems: "flex-end" }}>
            {[
              {
                label: "Assessment Date",
                value: result.timestamp.toLocaleDateString(undefined, {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }),
              },
              {
                label: "Assessment Time",
                value: result.timestamp.toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                }),
              },
              // { label: "Referral Code", value: referralCode.toUpperCase() },
            ].map(({ label, value }) => (
              <Text
                key={label.toLowerCase().replace(/\s+/g, "-")}
                style={{ fontFamily: "Poppins", fontSize: 12 }}
              >
                <Text style={{ fontWeight: "semibold" }}>{label}: </Text>
                <Text>{value}</Text>
              </Text>
            ))}
          </View>
        </View>
        <View style={styles.contentContainer}>
          <View style={{ flexDirection: "row", gap: 48 }}>
            <View style={{ flex: 1 }}>
              {[
                { label: "Access Code", value: AgentCode },
                { label: "Day Code", value: dayCode },
                { label: "Age", value: `${age} years` },
                { label: "Gender", value: gender },
                { label: "Height", value: `${height} cms` },
                { label: "Weight", value: `${weight} kgs` },
              ].map(({ label, value }, index) => (
                <Text
                  key={label.toLowerCase().replaceAll(" ", "-")}
                  style={{
                    paddingTop: index > 0 ? 8 : 0,
                    paddingBottom: 5,
                    borderBottomWidth: 0.5,
                    borderBottomColor: "#D4D4D4",
                    fontFamily: "Poppins",
                    fontSize: 10,
                  }}
                >
                  <Text style={{ fontWeight: "semibold" }}>{label}: </Text>
                  <Text>{value}</Text>
                </Text>
              ))}
            </View>
            <View
              style={{
                flex: 1,
                paddingVertical: 10,
                paddingHorizontal: 10,
                borderRadius: 20,
                backgroundColor: "#f8f9fc",
                textAlign: "center",
              }}
            >
              <Text
                style={{
                  fontFamily: "Poppins",
                  fontWeight: "semibold",
                  fontSize: 12,
                  color: "#233F8C",
                  marginBottom: 20,
                }}
              >
                Diabetes Risk
              </Text>
              <Range
                ranges={constants["Scores"].ranges}
                selectedIndex={diabetesRiskSelIdx}
              />
              <Text
                style={{ fontFamily: "Poppins", fontSize: 7, marginTop: 12 }}
              >
                <Text>Detected </Text>
                <Text
                  style={{
                    fontWeight: "semibold",
                    textTransform: "uppercase",
                    color: constants["Scores"].ranges[diabetesRiskSelIdx].color,
                  }}
                >
                  {constants["Scores"].ranges[diabetesRiskSelIdx].label} Risk
                </Text>
                <Text> for diabetes control.</Text>
              </Text>
            </View>
            <View
              style={{
                flex: 1,
                paddingVertical: 10,
                paddingHorizontal: 10,
                borderRadius: 20,
                backgroundColor: "#f8f9fc",
                textAlign: "center",
              }}
            >
              <Text
                style={{
                  fontFamily: "Poppins",
                  fontWeight: "semibold",
                  fontSize: 12,
                  color: "#233F8C",
                }}
              >
                Blood Pressure Risk
              </Text>
              <Text
                style={{
                  fontFamily: "Poppins",
                  fontSize: 7,
                  lineHeight: 1.25,
                  marginTop: 4,
                  marginBottom: 16,
                }}
              >
                <Text>Your Blood Pressure looks to be</Text>
                <Text style={{ fontWeight: "semibold" }}>
                  {bpRiskSelIdx === 0 ? "within" : "outside"}
                </Text>
                <Text> the normal range.</Text>
              </Text>
              <Range
                ranges={constants["Scores"].ranges}
                selectedIndex={bpRiskSelIdx}
              />
              <Text
                style={{ fontFamily: "Poppins", fontSize: 7, marginTop: 12 }}
              >
                <Text>Detected </Text>
                <Text
                  style={{
                    fontWeight: "semibold",
                    textTransform: "uppercase",
                    color: constants["Scores"].ranges[bpRiskSelIdx].color,
                  }}
                >
                  {constants["Scores"].ranges[bpRiskSelIdx].label} Risk
                </Text>
                <Text> for blood pressure issues.</Text>
              </Text>
            </View>
          </View>
          <Text style={[styles.title, { marginTop: 16 }]}>
            Other Health Scores
          </Text>
          <Score
            icon={HeartHealth}
            title="Heart Health"
            value={result.heart_score}
          />
          <View style={styles.horizontalLine} />
          <Score
            icon={MentalWellbeing}
            title="Mental Wellbeing"
            value={result.mind_score}
          />
          <View style={styles.horizontalLine} />
          <Score
            icon={Physiological}
            title="Physiological"
            value={result.physiological_score}
          />
          <Text style={[styles.title, { marginTop: 16 }]}>
            Health Parameters
          </Text>
          <Table
            title="Key Body Vitals"
            data={[
              [
                "HbA1C",
                <Text>
                  {result.hba1c < 5.7
                    ? "Predicted Value less than 5.7%"
                    : "Predicted value is more than 5.7%"}
                </Text>,
                undefined,
                "<5.7%",
              ],
              [
                "Blood Pressure",
                `${result.bp_sys}/${result.bp_dia} mmHg`,
                undefined,
                "90-120/60-80 mmHg",
              ],

              [
                "Respiration Rate",
                `${result.resp_rate}/min`,
                constants["Respiration Rate"].ranges.reduce((p, c) =>
                  result.resp_rate >= c.point ? c : p,
                ),
                "12-20/min",
              ],
              [
                "Oxygen Saturation",
                `${result.oxy_sat_prcnt}%`,
                constants["Oxygen Saturation"].ranges.reduce((p, c) =>
                  result.oxy_sat_prcnt >= c.point ? c : p,
                ),
                "95-100%",
              ],
            ]}
          />
          <Disclaimer />
        </View>
        <View style={styles.contentContainer} break>
          <Text style={styles.title}>Health Parameters</Text>
          <Table
            title="Physiological"
            data={[
              [
                "BMI",
                `${result.bmi} kg/m²`,
                constants["BMI"].ranges.reduce((p, c) =>
                  result.bmi > c.point ? c : p,
                ),
                "18.5-24.9 kg/m²",
              ],
            ]}
          />
          <Table
            containerStyle={{ marginTop: 16 }}
            title="Heart Health"
            data={[
              [
                "Heart Rate",
                `${result.heart_rate} bpm`,
                constants["Heart Rate"].ranges[result.posture]?.reduce?.(
                  (p, c) => (result.heart_rate >= c.point ? c : p),
                ),
                "60-100 bpm",
              ],
              [
                "SDNN",
                `${result.sdnn} msec`,
                result.sdnn !== "--"
                  ? constants["SDNN"].ranges.reduce((p, c) =>
                      result.sdnn >= c.point ? c : p,
                    )
                  : undefined,
                "60-100 msec",
              ],
              [
                "RMSSD",
                `${result.rmssd} msec`,
                result.rmssd !== "--"
                  ? constants["RMSSD"].ranges.reduce((p, c) =>
                      result.rmssd >= c.point ? c : p,
                    )
                  : undefined,
                "20-89 msec",
              ],
              [
                "PNN50",
                `${result.pNN50_per}%`,
                result.pNN50_per !== "--"
                  ? constants["PNN50"].ranges.reduce((p, c) =>
                      result.pNN50_per >= c.point ? c : p,
                    )
                  : undefined,
                ">3%",
              ],
              [
                "Cardiac Output",
                `${result.cardiac_out} L/min`,
                result.cardiac_out !== "--"
                  ? constants["Cardiac Output"].ranges.reduce((p, c) =>
                      result.cardiac_out >= c.point ? c : p,
                    )
                  : undefined,
                "4-8 L/min",
              ],
              [
                "Mean Arterial Pressure",
                `${result.map} mmHg`,
                result.map !== "--"
                  ? constants["Mean Arterial Pressure"].ranges.reduce((p, c) =>
                      result.map >= c.point ? c : p,
                    )
                  : undefined,
                "70-100 mmHg",
              ],
              [
                "Heart Utilization",
                `${result.heart_utilized}%\n `,
                result.heart_utilized !== "--"
                  ? constants["Heart Utilized"].ranges.reduce((p, c) =>
                      result.heart_utilized >= c.point ? c : p,
                    )
                  : undefined,
                <>
                  <Text>{"<50% "}</Text>
                  <Text style={{ fontSize: 8 }}>(At rest)</Text>
                  <Text>{"\n51-85% "}</Text>
                  <Text style={{ fontSize: 8 }}>(Moderate intensity)</Text>
                </>,
              ],
              ["Heart Rate Max", `${result.HRMax} bpm`, undefined, "--"],
              ["Heart Rate Reserve", `${result.HRR} bpm`, undefined, "--"],
              ["Target HR Range", `${result.THRR} bpm`, undefined, "--"],
              [
                "VO2Max",
                `${result.vo2max} mL/kg/min`,
                undefined,
                <>
                  <Text>{"42.5 ml/kg/min "}</Text>
                  <Text style={{ fontSize: 8 }}>(Male)</Text>
                  <Text>{"\n33 ml/kg/min "}</Text>
                  <Text style={{ fontSize: 8 }}>(Female)</Text>
                </>,
              ],
            ]}
          />
          <Disclaimer />
        </View>
        <View style={styles.contentContainer} break>
          <Text style={styles.title}>Health Risk Description</Text>
          <ParameterInfo
            icon={HeartRate}
            title="Heart Health"
            desc="A measure of the likelihood of developing cardiovascular conditions based on heart rate variability, rhythm abnormalities, and other cardiac parameters."
            ranges={[
              {
                label: "High Risk",
                desc: "Indicates significant irregularities or abnormalities in heart function, requiring immediate medical attention.",
              },
              {
                label: "Moderate Risk",
                desc: "Some irregularities in cardiac health; lifestyle adjustments and periodic monitoring recommended.",
              },
              {
                label: "Low Risk",
                desc: "Healthy heart function with minimal or no irregularities observed.",
              },
            ]}
          />

          <ParameterInfo
            icon={Diabetes}
            title="Diabetes Risk"
            desc="The Diabetes Risk Score (DRS) is a numerical value used to assess how well an individual is managing their condition and maintaining optimal blood glucose levels. It provides insight into the effectiveness of diabetes management strategies."
            ranges={[
              {
                label: "High Risk",
                desc: "Immediate medical intervention is required. Consult a healthcare provider as soon as possible to develop a personalized diabetes management plan. Lifestyle modifications and medication may be necessary. Focus on dietary changes, exercise, and possibly medication adjustments.",
              },
              {
                label: "Moderate Risk",
                desc: "Maintain a balanced diet, regular physical activity, and medication (if prescribed). Regular blood sugar monitoring is key to keeping your levels stable.",
              },
              {
                label: "Low Risk",
                desc: "Keep up your healthy habits, monitor your blood sugar regularly, and attend routine check-ups to ensure continued well-being.",
              },
            ]}
          />

          <ParameterInfo
            icon={BloodPressure}
            title="Blood Pressure"
            desc="A measure of the likelihood of experiencing complications related to hyper-tension or hypo-tension based on systolic and diastolic readings."
            ranges={[
              {
                label: "High Risk",
                desc: "Persistent or severe high/low blood pressure, with potential for serious health issues; medical intervention required.",
              },
              {
                label: "Moderate Risk",
                desc: "Mild irregularities in blood pressure that can be managed with lifestyle changes and monitoring.",
              },
              {
                label: "Low Risk",
                desc: "Stable blood pressure within the normal range.",
              },
            ]}
          />

          <ParameterInfo
            containerStyle={{ marginBottom: 60 }}
            icon={StressIndex}
            title="Mental Wellbeing"
            desc="An assessment of stress levels and autonomic nervous system (ANS) balance, reflecting the body's ability to manage stress and maintain mental health stability."
            ranges={[
              {
                label: "High Risk",
                desc: "Chronic stress with significant imbalances in ANS regulation, such as reduced parasympathetic activity or heightened sympathetic dominance; professional intervention strongly recommended.",
              },
              {
                label: "Moderate Risk",
                desc: "Occasional stress or mild ANS imbalances, indicating the need for improved stress management techniques and lifestyle adjustments.",
              },
              {
                label: "Low Risk",
                desc: "Balanced ANS function with effective stress management and resilience, reflecting good mental well-being.",
              },
            ]}
          />
          <ParameterInfo
            icon={VO2MAX}
            title="Physiological"
            desc="A quantitative measure of an individual's overall physiological fitness and well-being. It evaluates key aspects of physical health, including functional capacity, endurance, and overall health status, offering valuable insights into an individual's physical performance and resilience."
            ranges={[
              {
                label: "High Risk",
                desc: "Significant deficiencies in functional capacity, endurance, or overall health status, indicating poor physiological fitness and a heightened risk of health complications; immediate intervention is advised.",
              },
              {
                label: "Moderate Risk",
                desc: "Mild to moderate limitations in physical performance or endurance, suggesting areas for improvement through targeted exercise, nutrition, and lifestyle changes.",
              },
              {
                label: "Low Risk",
                desc: "Optimal physiological fitness with strong functional capacity, endurance, and overall health status, reflecting excellent physical well-being.",
              },
            ]}
          />
          <Disclaimer containerStyle={{ marginTop: 400 }} />
        </View>
        <View style={styles.contentContainer} break>
          <Text style={styles.title}>Health Parameters Description</Text>
          <ParameterInfo
            icon={HeartRate}
            title="Heart Rate"
            desc="The heart rate is the number of times heart beats in a minute. A normal resting heart rate for adults ranges from 60 to 100 beats per minute."
          />
          <ParameterInfo
            icon={RespirationRate}
            title="Respiration Rate"
            desc="The respiration rate is the number of breaths taken in a minute. The rate is usually measured when a person is at rest and simply involves counting the number of breaths for one minute by counting how many times the chest rises."
          />
          <ParameterInfo
            icon={OxygenSaturation}
            title="Oxygen Saturation"
            desc="Oxygen saturation (SpO2) is the measurement of how much oxygen the blood is carrying as a percentage of the maximum it could carry."
          />
          <ParameterInfo
            icon={BMI}
            title="BMI"
            desc="BMI is the measure of body fat based on height and weight. The normal range of BMI is 18.5 - 24.9 kg/m²."
          />
          <ParameterInfo
            icon={HRV}
            title="SDNN"
            desc="The standard deviation of all of the NN intervals (the time intervals between each heartbeat). Higher numbers usually indicate that your body is coping better with stress."
          />
          <ParameterInfo
            icon={HRV}
            title="RMSSD"
            desc="The root mean square of successive intervals between normal heartbeats (RMSSD). RMSSD is obtained by first calculating each successive time intervals between heartbeats in msec."
          />
          <ParameterInfo
            icon={HRV}
            title="PNN50"
            desc="PNN50 is the proportion of adjacent N-N intervals differing by more than 50 msec. pNN50 lower than 3% is considered indicative of high risk."
          />
          <Disclaimer />
        </View>
        <View style={styles.contentContainer} break>
          <Text style={styles.title}>Health Parameters Description</Text>
          <ParameterInfo
            icon={CardiacOutput}
            title="Cardiac Output"
            desc="The amount of blood heart pumps through the circulatory system in a minute. Cardiac output in humans is generally 4-8 L/min in an at-rest."
          />
          <ParameterInfo
            icon={MAP}
            title="Mean Arterial Pressure"
            desc="MAP is the average arterial pressure throughout one heart cycle. MAP anything between 70 and 100 mmHg is considered to be normal."
          />
          <ParameterInfo
            icon={HeartUtilization}
            title="Heart Utilization"
            desc="Heart utilization is the percentage of overall heart usage to its maximum peak heart rate. Generally < 50% utilization at rest and 51-85% utilization at moderate intensity is considered as normal."
          />
          <ParameterInfo
            icon={HRRelated}
            title="Heart Rate Max"
            desc="The highest number of beats the heart can pump per minute is the maximum heart rate."
          />
          <ParameterInfo
            icon={HRRelated}
            title="Heart Rate Reserve"
            desc="Heart rate reserve is the difference between your maximum (peak) heart rate and your resting heart rate."
          />
          <ParameterInfo
            icon={HRRelated}
            title="Target Heart Rate"
            desc="To achieve a certain level of exertion, there is a minimum number of heartbeats required within a specific timeframe."
          />
          <ParameterInfo
            icon={VO2MAX}
            title="VO2Max"
            desc="VO2Max is the amount of oxygen the body uses during exercise. It is a measure-ment to understand Physical fitness."
          />
          <Disclaimer />
        </View>
        <View fixed style={styles.footer}>
          <Text style={styles.footerText}>
            {`Report Generated from ${
              window.location.hostname
            } on ${new Date().toISOString()}`}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const Range = ({
  ranges = [
    { point: 0, label: "Start", color: "#ccc" },
    { point: 100, label: "End", color: "#000" },
  ],
  selectedIndex,
  className = "",
}) => {
  selectedIndex -= ranges.length - 1 === selectedIndex;
  return (
    <div className={`relative grid grid-flow-col auto-cols-fr ${className}`}>
      {ranges.slice(0, -1).map((rangeItem, index) => (
        <div key={`range-${index}`} className="flex flex-col justify-end">
          {index === selectedIndex && (
            <Caret
              className="self-center flex-shrink-0 w-2 h-1"
              style={{ color: ranges[selectedIndex].color }}
            />
          )}
          <h6
            className="text-primary/60 text-center text-[10px] font-medium uppercase truncate"
            style={
              index === selectedIndex
                ? { color: ranges[selectedIndex].color }
                : {}
            }
          >
            {rangeItem.label}
          </h6>
          <div
            className="my-1 h-1 bg-white"
            style={
              index === selectedIndex
                ? { backgroundColor: ranges[selectedIndex].color }
                : {}
            }
          />
          <p className="text-primary/70 text-[10px]">{rangeItem.point}</p>
        </div>
      ))}
      <p className="absolute bottom-0 right-0 text-primary/70 text-[10px]">
        {ranges[ranges.length - 1].point}
      </p>
    </div>
  );
};

const ListItemContainer = ({
  disabled = false,
  label = "",
  defaultExpanded = false,
  title,
  desc,
  className = "",
  ...props
}) => {
  return (
    <Disclosure
      defaultOpen={defaultExpanded}
      as="div"
      className="px-6 py-4 rounded-2xl bg-gradient-to-b from-[#F0F0F0] to-[#F8F8F8] shadow-box"
    >
      <Disclosure.Button as="div" disabled={disabled}>
        {({ open }) => [
          <div
            key="list-item-container-head"
            className="flex items-start justify-between space-x-1"
          >
            <h2 className="text-primary text-xs font-medium">{label}</h2>
            <Chevron
              className={`h-3 w-3 text-primary/60 transition-transform duration-300 ${
                open ? "rotate-180" : "rotate-0"
              }`}
            />
          </div>,
          <Fragment key="list-item-container-body">{props.children}</Fragment>,
        ]}
      </Disclosure.Button>
      <Disclosure.Panel>
        {typeof title === "string" && (
          <h5 className="mt-2.5 text-primary text-xs font-medium">{title}</h5>
        )}
        {typeof desc === "string" && (
          <p className="mt-1.5 text-primary/70 text-[10px] whitespace-pre-line">
            {desc}
          </p>
        )}
      </Disclosure.Panel>
    </Disclosure>
  );
};

const BPRange = ({ ranges = [], selectedIndex = -1 }) => {
  return (
    <div className="flex items-start mt-4">
      {ranges.map((r, index) => (
        <div key={index} className="flex-1 text-center">
          <p className="text-[10px] uppercase mb-1" style={{ color: r.color }}>
            {r.label}
          </p>

          <div
            className="h-1"
            style={{
              backgroundColor: r.color,
              borderTopLeftRadius: index === 0 ? 20 : 0,
              borderBottomLeftRadius: index === 0 ? 20 : 0,
              borderTopRightRadius: index === ranges.length - 1 ? 20 : 0,
              borderBottomRightRadius: index === ranges.length - 1 ? 20 : 0,
            }}
          />

          {selectedIndex === index && (
            <div
              className="mx-auto mb-1 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[8px] border-transparent"
              style={{ borderBottomColor: r.color }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

const Scores = ({ list = [], sys, dia }) => {
  const [openItems, setOpenItems] = useState({});
  const toggleItem = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };
  const BP_RANGES = [
    { label: "Low", color: "#4CAF50" },
    { label: "Moderate", color: "#F4B400" },
    { label: "High", color: "#E53935" },
  ];
  const getBPRiskIndex = (value) => {
    if (value < 108 || value > 135) return 2;
    if (value < 115 || value > 125) return 1;
    return 0;
  };

  const isBP = list?.[0]?.title === "Blood Pressure";
  const getRangeIndex = (value, ranges) => {
    return ranges.reduce((p, c, ci) => (value >= c.point ? ci : p), 0);
  };

  const selectedSYSRangeIndex =
    isBP && sys !== "--"
      ? getRangeIndex(sys, constants["Blood Pressure"].ranges[0])
      : undefined;

  const selectedDIARangeIndex =
    isBP && dia !== "--"
      ? getRangeIndex(dia, constants["Blood Pressure"].ranges[1])
      : undefined;

  return (
    <div className="px-6 py-2 rounded-xl bg-primary text-white relative z-999">
      <div className="divide-y divide-white/20">
        {list.map((score, index) => {
          const selectedScoreRange =
            constants["Scores"].ranges[
              score.title === "Blood Pressure"
                ? score.value < 108 || score.value > 135
                  ? 2
                  : score.value < 115 || score.value > 125
                    ? 1
                    : 0
                : score.value >= 75
                  ? 0
                  : score.value >= 50
                    ? 1
                    : 2
            ];
          return (
            <div key={`score-${index}`} className="py-4">
              <div className="flex justify-around items-center">
                <img
                  className="shrink-0 h-8 w-8"
                  src={score.logo}
                  alt={score.title}
                />
                <h2 className="grow mx-2.5 text-sm font-semibold leading-tight">
                  {score.title}
                </h2>

                <div
                  className={`w-3 h-3 rounded-full`}
                  style={{ backgroundColor: selectedScoreRange.color }}
                ></div>
                <p
                  className="shrink-0 ml-1 text-sm font-medium"
                  style={{ color: selectedScoreRange.color }}
                >
                  {selectedScoreRange.label} Risk
                </p>
                <Info
                  className="cursor-pointer w-5 h-5 ml-2"
                  onClick={() => toggleItem(index)}
                />
              </div>
              {score.title === "Blood Pressure" && (
                <>
                  <BPRange
                    ranges={BP_RANGES}
                    selectedIndex={getBPRiskIndex(score.value)}
                  />
                </>
              )}

              {/* {openItems[index] && (
                <div className="mt-4 p-4 bg-black/10 rounded-xl text-xs leading-relaxed text-white">
                  {score.desc}
                </div>
              )} */}
              {openItems[index] && (
                <div className="mt-4 p-4 bg-black/10 rounded-xl text-white">
                  {score.title === "Blood Pressure" &&
                  selectedSYSRangeIndex !== undefined &&
                  selectedDIARangeIndex !== undefined ? (
                    <>
                      {/* SYSTOLIC */}
                      <div className="mb-4 flex items-start">
                        <div className="w-20 text-left">
                          <div className="flex items-baseline gap-1">
                            <h4 className="text-lg font-bold text-white">
                              {sys}
                            </h4>

                            <span className="text-[12px] text-white/80">
                              {constants["Blood Pressure"].unit}
                            </span>
                          </div>

                          <p className="text-[12px] text-blue-200 font-medium">
                            Systolic
                          </p>
                        </div>

                        <div className="flex-grow ml-3 [&_h6]:text-white [&_p]:text-white">
                          <Range
                            ranges={constants["Blood Pressure"].ranges[0]}
                            selectedIndex={selectedSYSRangeIndex}
                          />
                        </div>
                      </div>

                      {/* DIASTOLIC */}
                      <div className="mb-4 flex items-start">
                        <div className="w-20 text-left">
                          <div className="flex items-baseline gap-1">
                            <h4 className="text-lg font-bold text-white">
                              {dia}
                            </h4>

                            <span className="text-[12px] text-white/80">
                              {constants["Blood Pressure"].unit}
                            </span>
                          </div>

                          <p className="text-[12px] text-blue-200 font-medium">
                            Diastolic
                          </p>
                        </div>

                        <div className="flex-grow ml-3 [&_h6]:text-white [&_p]:text-white">
                          <Range
                            ranges={constants["Blood Pressure"].ranges[1]}
                            selectedIndex={selectedDIARangeIndex}
                          />
                        </div>
                      </div>
                    </>
                  ) : null}
                  <p className="text-xs leading-relaxed mt-2">{score.desc}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Result = () => {
  const location = useLocation();
  const result = location.state?.result;
  const userData = location.state;
  const { fname, lname, dob, gender, height, weight, hcpCode, employeeCode } =
    userData ?? {};
  const reportPDF = useMemo(() => {
    const name = `${fname} ${lname}`;
    const ageDifMs = Date.now() - new Date(dob).getTime();
    const ageDate = new Date(ageDifMs);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);
    const gen = `${gender.slice(0, 1).toUpperCase()}${gender.slice(1)}`;
    const vitalsData = {
      timestamp: new Date(result?.entry_time?.replace?.(" ", "T") ?? 0),
      posture: result?.posture?.toLowerCase?.(),
      mind_score: result?.metadata.mental_wellbeing_score,
      heart_score: result?.metadata.overall_heart_score,
      physiological_score: result?.metadata.physiological_score,
      bp_sys: result?.vitals.bp_sys,
      bp_dia: result?.vitals.bp_dia,
      resp_rate: result?.vitals.resp_rate,
      oxy_sat_prcnt: result?.vitals.oxy_sat_prcnt,
      bmi: result?.metadata.physiological_scores.bmi,
      heart_rate: result?.vitals.heart_rate,
      sdnn:
        result?.metadata.heart_scores.sdnn !== "--"
          ? Math.round(result?.metadata.heart_scores.sdnn)
          : "--",
      rmssd:
        result?.metadata.heart_scores.rmssd !== "--"
          ? Math.round(result?.metadata.heart_scores.rmssd)
          : "--",
      pNN50_per: result?.metadata.heart_scores.pNN50_per,
      cardiac_out: result?.metadata.cardiovascular.cardiac_out,
      map: result?.metadata.cardiovascular.map,
      heart_utilized: result?.metadata.heart_scores.heart_utilized,
      HRMax: result?.metadata.heart_scores.HRMax,
      HRR: result?.metadata.heart_scores.HRR,
      THRR: result?.metadata.heart_scores.THRR,
      vo2max: result?.metadata.physiological_scores.vo2max,
      hba1c: result?.metadata.glucose_info.hba1c,
      diabetes_control_score:
        result?.metadata.glucose_info?.diabetes_control_score,
      scan_mode: result?.scan_mode,
    };
    return (
      <PDFReport
        AgentCode={employeeCode ?? ""}
        dayCode={hcpCode ?? ""}
        name={name}
        age={age}
        gender={gen}
        height={height}
        weight={weight}
        result={vitalsData}
      />
    );
  }, [
    fname,
    lname,
    dob,
    gender,
    height,
    weight,
    result,
    hcpCode,
    employeeCode,
  ]);

  const [modalState, setModalState] = useState({
    visible: false,
    title: "",
    desc: "",
  });
  const closeModal = () =>
    setModalState({ visible: false, title: "", desc: "" });

  return result?.scan_id?.length > 0 ? (
    <section className="px-6 py-2">
      <Scores
        sys={result?.vitals?.bp_sys}
        dia={result?.vitals?.bp_dia}
        list={[
          {
            title: "Blood Pressure",
            desc: "Blood pressure risk refers to the potential health problems caused by abnormal blood pressure levels. High blood pressure (hypertension) increases the risk of heart disease, stroke, and kidney damage, while low blood pressure (hypotension) can cause dizziness and fainting. Regular monitoring and healthy habits help manage these risks.\n\nPlease correlate clinically.",
            value: result?.vitals.bp_sys,
            logo: BloodPressureScore,
          },
          {
            title: "Diabetes Risk",
            desc: "The Diabetes Risk Score is a numerical value used to assess how well an individual is managing their condition and maintaining optimal blood glucose levels.",
            value: result?.metadata.glucose_info?.diabetes_control_score,
            logo: DiabetesRiskScore,
          },
          {
            title: "Heart Health",
            desc: "The Heart Health provides a comprehensive assessment of an individual's cardiac health status, helps to monitor cardiac function, track progress over time, and tailor interventions or treatment plans accordingly. A low risk heart health typically indicates better cardiac health and function, while a high risk may suggest the need for further evaluation or intervention to improve cardiovascular well-being.",
            value: result?.metadata.overall_heart_score,
            logo: HeartHealthScore,
          },
          {
            title: "Mental Wellbeing",
            desc: "The Mental Wellbeing Score, derived from an individual's stress level, mood insights, sleep quality provides a quantitative measure of an individual's overall mental health. It encompasses various dimensions of mental wellness, including emotional resilience, coping mechanisms, and overall psychological functioning.",
            value: result?.metadata.mental_wellbeing_score,
            logo: MentalWellbeingScore,
          },
          {
            title: "Physiological",
            desc: "The Physiological Score, also known as the Physio Fitness Score, is a quantitative measure that evaluates an individual's overall physiological fitness and well-being. It assesses various aspects of physical health and performance, providing insights into an individual's functional capacity, endurance, and overall health status.",
            value: result?.metadata.physiological_score,
            logo: PhysiologicalScore,
          },
        ]}
      />
      <h2 className="mt-6 mb-4 text-primary text-xl font-semibold">
        Detailed Report
      </h2>
      <Tab.Group>
        <Tab.List className="grid grid-cols-2 gap-3">
          {[
            "Key Body Vitals",
            "Heart Health",
            "Physiological",
            ...((result?.metadata?.heart_scores?.stress_index ?? "--") !== "--"
              ? ["Stress & ANS"]
              : []),
            "Blood Biomarkers",
          ].map((label, index) => (
            <Tab
              key={`tab-header-${index}`}
              className={({ selected }) =>
                `px-4 py-2 rounded-full border ${
                  selected
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-primary border-lightgray/25"
                } text-xs font-medium whitespace-nowrap outline-none`
              }
            >
              {label}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels className="my-6 space-y-6">
          <Tab.Panel className="space-y-6">
            {[
              { label: "Heart Rate", value: result?.vitals.heart_rate },
              { label: "Respiration Rate", value: result?.vitals.resp_rate },
              {
                label: "Oxygen Saturation",
                value: result?.vitals.oxy_sat_prcnt,
              },
            ].map((item, index) => {
              const ranges =
                item.label === "Heart Rate"
                  ? constants[item.label].ranges[result?.posture ?? "resting"]
                  : constants[item.label].ranges;
              const selectedRangeIndex = ranges.reduce(
                (p, c, ci) => (item.value >= c.point ? ci : p),
                0,
              );
              return (
                <ListItemContainer
                  key={`section-item-${index}`}
                  label={item.label}
                  title={`Your ${item.label} is ${ranges[selectedRangeIndex].label}`}
                  desc={
                    constants[item.label].info +
                    "\n\n" +
                    ranges[selectedRangeIndex].desc
                  }
                >
                  <div className="py-3 flex items-center justify-between space-x-2">
                    <div className="flex-shrink-0 basis-1/4 text-center font-semibold">
                      <h4 className="text-darkgray text-xl">{item.value}</h4>
                      <h5 className="text-primary/70 text-[10px]">
                        {constants[item.label].unit}
                      </h5>
                    </div>
                    <Range
                      className="flex-grow"
                      ranges={ranges}
                      selectedIndex={selectedRangeIndex}
                    />
                  </div>
                </ListItemContainer>
              );
            })}
          </Tab.Panel>
          <Tab.Panel className="space-y-6">
            {[
              {
                label: "SDNN",
                value:
                  result?.metadata.heart_scores.sdnn !== "--"
                    ? Math.round(result?.metadata.heart_scores.sdnn)
                    : "--",
              },
              {
                label: "RMSSD",
                value:
                  result?.metadata.heart_scores.rmssd !== "--"
                    ? Math.round(result?.metadata.heart_scores.rmssd)
                    : "--",
              },
              {
                label: "PNN50",
                value: result?.metadata.heart_scores.pNN50_per,
              },
              {
                label: "Cardiac Output",
                value: result?.metadata.cardiovascular.cardiac_out,
              },
              {
                label: "Mean Arterial Pressure",
                value: result?.metadata.cardiovascular.map,
              },
              {
                label: "Heart Utilized",
                value: result?.metadata.heart_scores.heart_utilized,
              },
            ].map((item, index) => {
              const selectedRangeIndex =
                item.value !== "--"
                  ? constants[item.label].ranges.reduce(
                      (p, c, ci) => (item.value >= c.point ? ci : p),
                      0,
                    )
                  : undefined;
              return (
                <ListItemContainer
                  key={`section-item-${index}`}
                  label={item.label}
                  title={`Your ${item.label} is ${
                    constants[item.label].ranges[selectedRangeIndex]?.label
                  }`}
                  desc={
                    constants[item.label].info +
                    "\n\n" +
                    constants[item.label].ranges[selectedRangeIndex]?.desc
                  }
                  disabled={selectedRangeIndex === undefined}
                >
                  <div className="py-3 flex items-center justify-between space-x-2">
                    <div className="flex-shrink-0 basis-1/4 text-center font-semibold">
                      <h4 className="text-darkgray text-xl">{item.value}</h4>
                      <h5 className="text-primary/70 text-[10px]">
                        {constants[item.label].unit}
                      </h5>
                    </div>
                    <Range
                      className="flex-grow"
                      ranges={constants[item.label].ranges}
                      selectedIndex={selectedRangeIndex}
                    />
                  </div>
                </ListItemContainer>
              );
            })}
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  label: "Heart Rate Max",
                  value: result?.metadata.heart_scores.HRMax,
                  gradient: "from-[#FDEBAD] to-[#FFF5D4]",
                },
                {
                  label: "Heart Rate Reserve",
                  value: result?.metadata.heart_scores.HRR,
                  gradient: "from-[#E1CBF3]/[0.61] to-[#F4E9FE]/[0.79]",
                },
                {
                  label: "Target HR Range",
                  value: result?.metadata.heart_scores.THRR,
                  gradient: "from-[#BEEB9C]/[0.61] to-[#EFFCE4]",
                },
                {
                  label: "VO₂ Max",
                  value: result?.metadata.physiological_scores.vo2max,
                  gradient: "from-[#C2EAFB] to-[#E1F6FF]",
                },
              ].map((item, index) => (
                <button
                  type="button"
                  key={`section-smallitem-${index}`}
                  className={`px-3 py-4 rounded-2xl bg-gradient-to-r ${item.gradient} flex flex-col items-stretch justify-between text-left`}
                  onClick={() =>
                    setModalState({
                      visible: true,
                      title: item.label,
                      desc: constants[item.label].description,
                    })
                  }
                >
                  <div className="flex items-start justify-between space-x-1">
                    <h2 className="text-primary text-xs font-medium">
                      {item.label}
                    </h2>
                    <Info className="my-0.5 h-3 w-3 text-primary/60" />
                  </div>
                  <div className="mt-3 break-all font-semibold">
                    <h4 className="inline text-darkgray text-xl">
                      {item.value}
                    </h4>
                    &nbsp;
                    <h5 className="inline text-primary/70 text-[10px]">
                      {constants[item.label].unit}
                    </h5>
                  </div>
                </button>
              ))}
            </div>
          </Tab.Panel>
          <Tab.Panel className="space-y-6">
            {(() => {
              const bmi = result?.metadata.physiological_scores.bmi;
              const selectedBMIRangeIndex = constants.BMI.ranges.reduce(
                (p, c, ci) => (bmi > c.point ? ci : p),
                0,
              );
              return (
                <ListItemContainer
                  label="BMI"
                  title={`Your BMI is ${constants.BMI.ranges[selectedBMIRangeIndex].label}`}
                  desc={
                    constants.BMI.info +
                    "\n\n" +
                    constants.BMI.ranges[selectedBMIRangeIndex].desc
                  }
                >
                  <div className="py-3 flex items-center justify-between space-x-2">
                    <div className="flex-shrink-0 basis-1/4 text-center font-semibold">
                      <h4 className="text-darkgray text-xl">{bmi}</h4>
                      <h5 className="text-primary/70 text-[10px]">
                        {constants.BMI.unit}
                      </h5>
                    </div>
                    <Range
                      className="flex-grow"
                      ranges={constants.BMI.ranges}
                      selectedIndex={selectedBMIRangeIndex}
                    />
                  </div>
                </ListItemContainer>
              );
            })()}
            <div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    label: "Blood Volume",
                    value: result?.metadata.physiological_scores.bloodvolume,
                    gradient: "from-[#E1CBF3]/[0.61] to-[#F4E9FE]/[0.79]",
                  },
                  {
                    label: "Total Body Water",
                    value: result?.metadata.physiological_scores.tbw,
                    gradient: "from-[#C2EAFB] to-[#E1F6FF]",
                  },
                  ...(result?.metadata.physiological_scores.cal_carb !== "--" &&
                  result?.metadata.physiological_scores.cal_fat !== "--"
                    ? [
                        {
                          label: "Calorie from Carb",
                          value:
                            !(
                              result?.metadata.physiological_scores.cal_carb >=
                              0
                            ) ||
                            !(
                              result?.metadata.physiological_scores.cal_carb <=
                              100
                            )
                              ? "--"
                              : result.metadata.physiological_scores.cal_carb,
                          gradient: "from-[#BEEB9C]/[0.61] to-[#EFFCE4]",
                        },
                        {
                          label: "Calorie from Fat",
                          value:
                            !(
                              result?.metadata.physiological_scores.cal_fat >= 0
                            ) ||
                            !(
                              result?.metadata.physiological_scores.cal_fat <=
                              100
                            )
                              ? "--"
                              : result.metadata.physiological_scores.cal_fat,
                          gradient: "from-[#FDEBAD] to-[#FFF5D4]",
                        },
                      ]
                    : []),
                ].map((item, index) => (
                  <button
                    type="button"
                    key={`section-smallitem-${index}`}
                    className={`px-3 py-4 rounded-2xl bg-gradient-to-r ${item.gradient} flex flex-col items-stretch justify-between text-left`}
                    onClick={() =>
                      setModalState({
                        visible: true,
                        title: item.label,
                        desc: constants[item.label].description,
                      })
                    }
                  >
                    <div className="flex items-start justify-between space-x-1">
                      <h2 className="text-primary text-xs font-medium">
                        {item.label}
                      </h2>
                      <Info className="my-0.5 h-3 w-3 text-primary/60" />
                    </div>
                    <div className="mt-3 break-all font-semibold">
                      <h4 className="inline text-darkgray text-xl">
                        {item.value}
                      </h4>
                      &nbsp;
                      <h5 className="inline text-primary/70 text-[10px]">
                        {constants[item.label].unit}
                      </h5>
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {[
                  {
                    label: "Body Water %",
                    value: result?.metadata.physiological_scores.tbwp,
                  },
                  {
                    label: "Total Body Fat",
                    value: result?.metadata.physiological_scores.bodyfat,
                  },
                  {
                    label: "Body Weight",
                    value: result?.metadata.physiological_scores.weight,
                  },
                ].map((item, index) => (
                  <button
                    type="button"
                    key={`section-smallitem-${index}`}
                    className="px-3 py-4 rounded-2xl bg-[#EFFCE4] flex flex-col items-stretch justify-between text-left"
                    onClick={() =>
                      setModalState({
                        visible: true,
                        title: item.label,
                        desc: constants[item.label].description,
                      })
                    }
                  >
                    <div className="flex items-start justify-between space-x-1">
                      <h2 className="text-primary text-xs font-medium">
                        {item.label}
                      </h2>
                      <Info className="my-0.5 h-3 w-3 text-primary/60" />
                    </div>
                    <div className="mt-3 break-all font-semibold">
                      <h4 className="inline text-darkgray text-xl">
                        {item.value}
                      </h4>
                      &nbsp;
                      <h5 className="inline text-primary/70 text-[10px]">
                        {constants[item.label].unit}
                      </h5>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </Tab.Panel>
          {(result?.metadata?.heart_scores?.stress_index ?? "--") !== "--" && (
            <Tab.Panel className="space-y-6">
              {(() => {
                const stressLevel = result?.metadata.heart_scores.stress_index;
                const selectedRangeIndex = constants[
                  "Stress Level"
                ].ranges.reduce(
                  (p, c, ci) => (stressLevel > c.point ? ci : p),
                  0,
                );
                const selectedRange =
                  constants["Stress Level"].ranges[selectedRangeIndex];
                let detailTitle = "";
                let detailDescription = "";
                if ((result?.metadata?.heart_scores?.rmssd ?? "--") !== "--") {
                  if (result.metadata.heart_scores.rmssd < 65) {
                    if (
                      result.vitals.heart_rate > 100 ||
                      result.vitals.bp_sys > 130 ||
                      result.vitals.bp_dia > 90
                    ) {
                      detailTitle = "More Sympathetic Interactions";
                      detailDescription =
                        "Although HRV manifests as a function of your heart rate, it actually originates from your nervous system. Your autonomic nervous system, which controls the involuntary aspects of your physiology, has two branches, parasympathetic (deactivating) and sympathetic (activating).\n\nThe sympathetic nervous system (often called “fight or flight”) reflects responses to things like stress and exercise, and increases your heart rate and blood pressure.";
                    } else {
                      detailTitle = "More Parasympathetic Interactions";
                      detailDescription =
                        "Although HRV manifests as a function of your heart rate, it actually originates from your nervous system. Your autonomic nervous system, which controls the involuntary aspects of your physiology, has two branches, parasympathetic (deactivating) and sympathetic (activating)\n\nThe parasympathetic nervous system (often referred to as “rest and digest”) handles inputs from internal organs, like digestion or your fingernails and hair growing.";
                    }
                  } else {
                    detailTitle = "Balanced ANS Interactions";
                    detailDescription =
                      "When you have high heart rate variability, it means that your body is responsive to both sets of inputs (parasympathetic and sympathetic). This is a sign that your nervous system is balanced, and that your body is very capable of adapting to its environment and performing at its best.";
                  }
                } else {
                  detailDescription =
                    "Not enought data to process. This happens when the signal strength of the scan is poor due to improper light or too much movements.";
                }
                return (
                  <>
                    <div className="px-6 py-4 rounded-2xl bg-gradient-to-b from-[#F0F0F0] to-[#F8F8F8] shadow-box">
                      <div className="flex items-center justify-between space-x-4">
                        {selectedRange.icon}
                        <div className="flex-grow">
                          <h2 className="text-primary text-xs font-medium">
                            Stress Level
                          </h2>
                          <h4
                            className="mt-1 text-[10px] font-medium uppercase"
                            style={{ color: selectedRange.color }}
                          >
                            {selectedRange.label}
                          </h4>
                        </div>
                        <div className="flex-shrink-0 text-right font-semibold">
                          <h4 className="text-darkgray text-xl">
                            {stressLevel}
                          </h4>
                          <h5 className="text-primary/70 text-[10px]">
                            {constants["Stress Level"].unit}
                          </h5>
                        </div>
                      </div>
                      <div className="mt-4">
                        <h5 className="text-primary text-xs font-medium">
                          Your Stress Level is {selectedRange.label}
                        </h5>
                        <p className="mt-1.5 text-primary/70 text-[10px]">
                          {constants["Stress Level"].description}
                        </p>
                      </div>
                    </div>
                    <div className="p-6 rounded-xl bg-secondary/10 space-y-1.5">
                      {detailTitle.length > 0 && (
                        <h3 className="text-darkgray text-xs font-medium">
                          {detailTitle}
                        </h3>
                      )}
                      <p className="text-darkgray text-[10px] text-justify whitespace-pre-line">
                        {detailDescription}
                      </p>
                      <h5 className="text-darkgray text-xs text-center font-medium">
                        Autonomic Nervous Systems (ANS)
                      </h5>
                    </div>
                  </>
                );
              })()}
            </Tab.Panel>
          )}
          <Tab.Panel className="space-y-6">
            {(() => {
              if (typeof result?.metadata?.glucose_info?.hba1c !== "number")
                return null;
              const hba1c = result?.metadata.glucose_info.hba1c;
              return (
                <div className="sm:mx-[7.5%] px-4 sm:px-6 py-6 sm:py-8 border rounded-2xl shadow-md bg-white text-primary">
                  <div className="flex items-start justify-between space-x-3 sm:space-x-6">
                    {hba1c < 5.7 ? (
                      <HbA1cOptimal className="shrink-0 h-9 w-9 sm:h-14 sm:w-14 rounded-full bg-white" />
                    ) : (
                      <HbA1cNonOptimal className="shrink-0 h-9 w-9 sm:h-14 sm:w-14 rounded-full bg-white" />
                    )}
                    <div className="grow space-y-1.5 sm:space-y-2">
                      <h4 className="text-xs sm:text-lg font-semibold">
                        Predicted HbA1c Level
                      </h4>
                      <p className="text-black/70 text-[10px] sm:text-base tracking-widest uppercase font-medium">
                        {hba1c < 5.7 ? "Optimal" : "Above Optimal"}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-lg sm:text-2xl font-bold">
                        {hba1c < 5.7 ? "<5.7" : "≥5.7"}
                      </p>
                      <p className="text-[10px] sm:text-base leading-none opacity-80">
                        {constants["HbA1c"].unit}
                      </p>
                    </div>
                  </div>
                  <div className="relative my-1 py-4 mx-8 grid grid-flow-col auto-cols-fr">
                    <div style={{ color: constants["HbA1c"].ranges[0].color }}>
                      <h6 className="opacity-60 text-left text-[10px] sm:text-base font-medium uppercase truncate">
                        {constants["HbA1c"].ranges[0].label}
                      </h6>
                      <div className="my-1 sm:my-2 h-1 sm:h-2 bg-current rounded-l-full" />
                    </div>
                    <div style={{ color: constants["HbA1c"].ranges[1].color }}>
                      <h6 className="opacity-60 text-center text-[10px] sm:text-base font-medium uppercase truncate">
                        {constants["HbA1c"].ranges[1].label}
                      </h6>
                      <div className="my-1 sm:my-2 h-1 sm:h-2 bg-current rounded-none" />
                      <p className="ml-1 sm:ml-2 !text-black/70 text-[10px] sm:text-base">
                        {constants["HbA1c"].ranges[1].point}
                      </p>
                    </div>
                    <div style={{ color: constants["HbA1c"].ranges[2].color }}>
                      <h6 className="opacity-60 text-right text-[10px] sm:text-base font-medium uppercase truncate">
                        {constants["HbA1c"].ranges[2].label}
                      </h6>
                      <div className="my-1 sm:my-2 h-1 sm:h-2 bg-current rounded-r-full" />
                      <p className="-ml-2 sm:-ml-3 !text-black/70 text-[10px] sm:text-base">
                        {constants["HbA1c"].ranges[2].point}
                      </p>
                    </div>
                    <div className="absolute left-1/3 top-0 bottom-0 border-l-2 sm:border-l-4 border-dashed border-stroke" />
                    <svg
                      className={`absolute left-1/3 top-0.5 sm:top-1 h-2.5 sm:h-5 ${
                        hba1c < 5.7
                          ? "-translate-x-[250%] rotate-180"
                          : "translate-x-[150%]"
                      }`}
                      viewBox="0 0 6 11"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.5 0.5L0.5 10.5L5.5 5.42754L0.5 0.5Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <p className="mt-2.5 sm:mt-5 text-black/70 text-[10px] sm:text-base leading-snug">
                    {hba1c < 5.7
                      ? "Your HbA1c level is within the optimal range (<5.7%). Maintain a healthy lifestyle to support long-term well-being."
                      : "Your HbA1c level appears to be above the optimal range (≥5.7%). We recommend getting a comprehensive test or consulting a healthcare professional for proper evaluation and guidance."}
                  </p>
                </div>
              );
            })()}
          </Tab.Panel>
          <PDFDownloadLink
            className="flex items-center justify-between space-x-4 py-3 px-6 rounded-xl bg-[#2B2B2B] text-white text-sm font-medium"
            document={reportPDF}
            fileName={`CarePlix-ScanResult-Report-${new Date(
              result?.entry_time?.replace?.(" ", "T") ?? 0,
            ).toISOString()}.pdf`}
          >
            {({ loading }) => (
              <>
                <span>
                  {loading ? "Loading document..." : "Download PDF Report"}
                </span>
                <Download className="flex-shrink-0 h-6 w-6" />
              </>
            )}
          </PDFDownloadLink>
          <div className="p-6 rounded-2xl flex flex-col items-center text-center bg-primary text-white">
            <h4 className="text-xs font-medium">Disclaimer</h4>
            <p className="mt-2 text-[10px] text-justify">
              For Investigational Use Only. Careplix is not a substitute for the
              clinical judgment of a healthcare professional. Careplix is
              intended to improve your awareness of general wellness. Careplix
              does not diagnose, treat, mitigate or prevent any disease,
              symptom, disorder or abnormal physical state. Consult with a
              health care professional or emergency services if you believe you
              may have a medical issue.
            </p>
            <Link
              className="mt-4 px-4 py-2 rounded-full bg-white text-primary text-xs font-medium"
              to="/"
              replace
            >
              Dashboard
            </Link>
          </div>
        </Tab.Panels>
      </Tab.Group>
      <Transition show={modalState.visible} as={Fragment}>
        <Dialog as={Fragment} onClose={closeModal}>
          <Transition.Child
            className="fixed top-0 bottom-0 left-0 right-0 bg-black/50 flex items-center justify-center"
            enter="duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="duration-300 delay-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Transition.Child
              as={Fragment}
              enter="delay-150 duration-200"
              enterFrom="opacity-0 scale-50"
              enterTo="opacity-100 scale-100"
              leave="duration-100"
              leaveFrom="opacity-50 scale-50"
              leaveTo="opacity-0 scale-0"
            >
              <Dialog.Panel className="w-4/5 bg-white rounded-2xl p-6 flex flex-col items-center text-center">
                <Dialog.Title className="text-primary text-xs font-medium">
                  {modalState.title}
                </Dialog.Title>
                <Dialog.Description className="mt-2 text-primary/70 text-[10px]">
                  {modalState.desc}
                </Dialog.Description>
                <button
                  className="mt-4 px-4 py-2 rounded-full bg-primary text-white text-xs font-medium"
                  type="button"
                  onClick={closeModal}
                >
                  Close
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </Transition.Child>
        </Dialog>
      </Transition>
    </section>
  ) : (
    <Navigate to="/" replace />
  );
};

export default Result;
